import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Eye, RotateCcw, Phone, MapPin, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function getInitials(name) {
  return name?.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'M';
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch { return dateStr; }
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function openMaps(lat, lng) {
  if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
}

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [followups, setFollowups] = useState({ overdue: [], today: [], thisWeek: [] });
  const [fuTab, setFuTab] = useState('overdue');
  const [expandedFu, setExpandedFu] = useState(null);
  const [upiAlert, setUpiAlert] = useState(true);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [dashRes, feedRes, fuRes] = await Promise.all([
        api.get('/dashboard/owner'),
        api.get('/dashboard/feed'),
        api.get('/followups')
      ]);
      setData(dashRes.data);
      setFeed(feedRes.data || []);
      setFollowups(fuRes.data);
      if (fuRes.data.overdue?.length === 0 && fuRes.data.today?.length > 0) setFuTab('today');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh on visibility change (tab switch back)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <OwnerLayout title="Dashboard">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', fontFamily: 'var(--fh)', color: 'var(--navy)' }}>Loading...</div>
      </OwnerLayout>
    );
  }

  const yupi = data?.upiQrYesterday || [];
  const yupiTotal = yupi.reduce((s, a) => s + a.count, 0);
  const fuItems = followups[fuTab] || [];
  const pipeline = data?.upiPipeline || { total: 0, pending: 0, contacted: 0, onboarded: 0 };
  const pipelineConversion = pipeline.total > 0 ? Math.round(pipeline.onboarded / pipeline.total * 100) : 0;

  return (
    <OwnerLayout title="Dashboard" overdueCount={followups.overdue?.length || 0}>
      {/* UPI/QR Alert - Yesterday's leads */}
      {yupiTotal > 0 && upiAlert && (
        <div className="od-alert" data-testid="upi-alert">
          <div className="od-alert-left">
            <div className="od-alert-num">{yupiTotal}</div>
            <div className="od-alert-lbl">Yesterday</div>
          </div>
          <div className="od-alert-body">
            <div className="od-alert-title">New UPI/QR leads came in yesterday</div>
            <div className="od-alert-tags">
              {yupi.map((a, i) => (
                <span key={i} className="od-tag"><strong>{a.count}</strong> {a.agentName}</span>
              ))}
            </div>
          </div>
          <div className="od-alert-actions">
            <button className="btn-white" onClick={() => {
              const now = new Date();
              const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const yesterdayStart = new Date(todayStart.getTime() - 86400000);
              navigate(`/owner/merchants?service=upi_qr&createdFrom=${yesterdayStart.toISOString().slice(0, 19)}&createdTo=${todayStart.toISOString().slice(0, 19)}`);
            }}>
              View {yupiTotal} Leads
            </button>
            <button className="od-dismiss" onClick={() => setUpiAlert(false)}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="od-stats" data-testid="owner-stats">
        <div className="od-stat clickable" onClick={() => navigate('/owner/merchants')} data-testid="stat-total">
          <div className="od-stat-lbl">Total Merchants</div>
          <div className="od-stat-val">{data?.totalMerchants ?? 0}</div>
          <div className="od-stat-sub">+{data?.todayRegistrations ?? 0} today</div>
        </div>
        <div className="od-stat accent clickable" onClick={() => navigate('/owner/merchants?service=upi_qr')} data-testid="stat-upi">
          <div className="od-stat-lbl">UPI/QR Leads</div>
          <div className="od-stat-val red">{data?.upiQrTotal ?? 0}</div>
          <div className="od-stat-sub">+{data?.upiQrToday ?? 0} today</div>
        </div>
        <div className="od-stat clickable" onClick={() => navigate('/owner/merchants?status=Onboarded')} data-testid="stat-onboarded">
          <div className="od-stat-lbl">Onboarded</div>
          <div className="od-stat-val">{data?.totalOnboarded ?? 0}</div>
          <div className="od-stat-sub">{data?.totalMerchants > 0 ? Math.round(data.totalOnboarded / data.totalMerchants * 100) : 0}% conversion</div>
        </div>
        <div className="od-stat clickable" onClick={() => navigate('/owner/team')} data-testid="stat-agents">
          <div className="od-stat-lbl">Agents Active</div>
          <div className="od-stat-val">{data?.agentsActive ?? 0}</div>
          <div className="od-stat-sub">of {data?.totalAgents ?? 0} total</div>
        </div>
      </div>

      {/* Two-column layout: Activity + UPI Pipeline */}
      <div className="od-two-col">
        {/* Today's Activity */}
        <div className="od-activity panel" data-testid="live-feed-panel">
          <div className="panel-header">
            <span className="panel-title">
              Today's Activity
              <span className="live-badge"><span className="live-dot" /> Live</span>
            </span>
            <span className="panel-link" onClick={() => navigate('/owner/reports')}>Full report</span>
          </div>
          <div className="od-activity-body">
            {/* Mini stats */}
            <div className="od-mini-stats">
              <div className="od-mini clickable" onClick={() => navigate('/owner/merchants')}>
                <div className="od-mini-val">{data?.todayRegistrations ?? 0}</div>
                <div className="od-mini-lbl">Registrations</div>
              </div>
              <div className="od-mini clickable" onClick={() => navigate('/owner/merchants?service=upi_qr')}>
                <div className="od-mini-val red">{data?.upiQrToday ?? 0}</div>
                <div className="od-mini-lbl">UPI/QR</div>
              </div>
              <div className="od-mini clickable" onClick={() => navigate('/owner/followups')}>
                <div className="od-mini-val">{data?.todayFollowups ?? 0}</div>
                <div className="od-mini-lbl">Follow-ups</div>
              </div>
              <div className="od-mini clickable" onClick={() => navigate('/owner/team')}>
                <div className="od-mini-val">{data?.agentsActive ?? 0}</div>
                <div className="od-mini-lbl">Active</div>
              </div>
            </div>

            {/* Feed */}
            <div className="od-feed">
              {feed.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--g3)', fontSize: '13px' }}>No activity today</div>
              ) : (
                feed.slice(0, 6).map(item => (
                  <div key={item.id} className="od-feed-item">
                    <span className={`od-feed-dot ${item.isUpiQr ? 'upi' : ''}`} />
                    <div className="od-feed-info">
                      <div className="od-feed-text">
                        <strong>{item.agentName}</strong>
                        {item.status === 'Onboarded' ? ' onboarded ' : ' added '}
                        <strong>{item.businessName}</strong>
                        {item.isUpiQr && <span className="od-feed-upi"> — UPI/QR</span>}
                      </div>
                      <div className="od-feed-time">{formatDateTime(item.createdAt)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* UPI/QR Pipeline - Compact */}
        <div className="od-pipeline" data-testid="upi-pipeline">
          <div className="od-pipe-inner">
            <div className="od-pipe-head">
              <div className="od-pipe-icon"><Smartphone size={16} color="white" /></div>
              <div>
                <div className="od-pipe-title">UPI/QR Pipeline</div>
                <div className="od-pipe-sub">Primary product</div>
              </div>
            </div>
            <div className="od-pipe-grid">
              <div className="od-pipe-cell"><div className="od-pipe-lbl">TOTAL</div><div className="od-pipe-num">{pipeline.total}</div></div>
              <div className="od-pipe-cell"><div className="od-pipe-lbl">PENDING</div><div className="od-pipe-num">{pipeline.pending}</div></div>
              <div className="od-pipe-cell"><div className="od-pipe-lbl">CONTACTED</div><div className="od-pipe-num">{pipeline.contacted}</div></div>
              <div className="od-pipe-cell green"><div className="od-pipe-lbl">ONBOARDED</div><div className="od-pipe-num">{pipeline.onboarded}</div></div>
            </div>
            <div className="od-pipe-foot">
              <span>Conversion: <strong>{pipelineConversion}%</strong></span>
              <button className="od-pipe-btn" onClick={() => navigate('/owner/merchants?service=upi_qr')} data-testid="upi-view-all">View all</button>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-ups Panel - With expandable merchant details */}
      <div className="panel od-fu-panel" data-testid="followups-panel">
        <div className="panel-header">
          <span className="panel-title">
            Follow-ups
            {(followups.overdue?.length || 0) > 0 && (
              <span className="count-tag red">{followups.overdue.length} overdue</span>
            )}
          </span>
          <span className="panel-link" onClick={() => navigate('/owner/followups')}>View all</span>
        </div>

        <div className="fu-tabs">
          {[
            { key: 'overdue', label: 'Overdue', count: followups.overdue?.length || 0 },
            { key: 'today', label: 'Today', count: followups.today?.length || 0 },
            { key: 'thisWeek', label: 'This Week', count: followups.thisWeek?.length || 0 },
          ].map(tab => (
            <div
              key={tab.key}
              className={`fu-tab ${fuTab === tab.key ? 'active' : ''}`}
              onClick={() => setFuTab(tab.key)}
              data-testid={`fu-tab-${tab.key}`}
            >
              <span className="num">{tab.count}</span>
              <span className="lbl">{tab.label}</span>
            </div>
          ))}
        </div>

        <div className="od-fu-list">
          {fuItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--g3)', fontSize: '13px' }}>
              {fuTab === 'overdue' ? 'No overdue follow-ups' : 'No follow-ups'}
            </div>
          ) : (
            fuItems.slice(0, 8).map(m => {
              const isExpanded = expandedFu === m.id;
              return (
                <div key={m.id} className="od-fu-card" data-testid={`fu-item-${m.id}`}>
                  <div className="od-fu-row" onClick={() => setExpandedFu(isExpanded ? null : m.id)}>
                    <div className="od-fu-av">{getInitials(m.businessName)}</div>
                    <div className="od-fu-info">
                      <div className="od-fu-name">{m.businessName}</div>
                      <div className="od-fu-meta">
                        <a href={`tel:${m.phone}`} className="od-fu-phone" onClick={e => e.stopPropagation()}>
                          <Phone size={10} /> {m.phone}
                        </a>
                        <span className="od-fu-agent">{m.agentName || 'Agent'}</span>
                      </div>
                    </div>
                    <div className="od-fu-right">
                      {m.nextVisitDate && <div className="od-fu-date"><CalendarClock size={10} /> {formatDate(m.nextVisitDate)}</div>}
                      <span className={`badge-${(m.status || 'new').toLowerCase()}`}>{m.status}</span>
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="od-fu-detail">
                      <div className="od-fu-detail-row">
                        <span className="od-fu-dl">Owner:</span> {m.ownerName || '—'}
                      </div>
                      {m.address && (
                        <div className="od-fu-detail-row">
                          <span className="od-fu-dl">Address:</span> {m.address}{m.city ? `, ${m.city}` : ''}
                        </div>
                      )}
                      {m.gps_lat && (
                        <div className="od-fu-detail-row">
                          <button className="od-fu-gps" onClick={() => openMaps(m.gps_lat, m.gps_lng)}>
                            <MapPin size={11} /> View on Map
                          </button>
                        </div>
                      )}
                      {(m.services || []).length > 0 && (
                        <div className="od-fu-detail-row">
                          <span className="od-fu-dl">Services:</span>
                          <div className="od-fu-tags">
                            {m.services.map(s => <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>)}
                          </div>
                        </div>
                      )}
                      {/* Visit History / Notes */}
                      {m.visitLog && m.visitLog.length > 0 ? (
                        <div className="od-fu-notes">
                          <div className="od-fu-notes-title">Agent Notes ({m.visitLog.length})</div>
                          {m.visitLog.slice(-4).reverse().map((v, i) => (
                            <div key={i} className="od-fu-note">
                              <div className="od-fu-note-date">{formatDateTime(v.visitedAt || v.addedAt)}</div>
                              <div className="od-fu-note-text">{v.note}</div>
                              {(v.addedByName || v.createdByName) && <div className="od-fu-note-by">— {v.addedByName || v.createdByName}</div>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="od-fu-notes">
                          <div className="od-fu-notes-title">Agent Notes</div>
                          <div style={{ fontSize: '12px', color: 'var(--g3)', padding: '6px 0' }}>No notes recorded yet</div>
                        </div>
                      )}
                      <button className="od-fu-view-btn" onClick={() => navigate(`/owner/merchants?search=${encodeURIComponent(m.businessName)}`)}>
                        View Full Details
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Team Performance */}
      <div style={{ marginTop: '16px' }}>
        <div className="sec-header">
          <span className="sec-title">Team Performance</span>
          <span className="sec-link" onClick={() => navigate('/owner/team')}>Manage Team</span>
        </div>
        <div className="team-perf-scroll">
          {(data?.perAgentStats || []).map(agent => {
            const getStatusColor = (status) => {
              if (status === 'active') return '#16a34a';
              if (status === 'week') return '#d97706';
              return '#9ca3af';
            };
            const statusText = agent.status === 'active' ? `Active ${timeAgo(agent.lastActiveAt)}` :
                               agent.status === 'week' ? `${timeAgo(agent.lastActiveAt)}` :
                               agent.status === 'inactive' ? `Not active for ${Math.ceil((Date.now() - new Date(agent.lastActiveAt || 0).getTime()) / 86400000)} days` : 'Inactive';
            return (
              <div key={agent.id} className="team-card" data-testid={`team-card-${agent.id}`}>
                <div className="team-card-bar" style={{ background: getStatusColor(agent.status) }} />
                <div className="team-card-header">
                  <div className="team-av">
                    {getInitials(agent.name)}
                    <span className="team-status-dot" style={{ background: getStatusColor(agent.status) }} />
                  </div>
                  <div className="team-info">
                    <div className="team-name">{agent.name}</div>
                    <div className="team-id">{agent.employeeId}</div>
                  </div>
                </div>
                <div className="team-stats-row">
                  <div className="team-stat">
                    <div className="team-stat-val">{agent.today}</div>
                    <div className="team-stat-label">Today</div>
                  </div>
                  <div className="team-stat">
                    <div className="team-stat-val">{agent.total}</div>
                    <div className="team-stat-label">Total</div>
                  </div>
                  <div className="team-stat">
                    <div className="team-stat-val">{agent.onboarded}</div>
                    <div className="team-stat-label">Onboarded</div>
                  </div>
                  <div className="team-stat accent">
                    <div className="team-stat-val" style={{ color: '#16a34a', fontWeight: 800 }}>
                      {agent.total > 0 ? Math.round(agent.onboarded / agent.total * 100) : 0}%
                    </div>
                    <div className="team-stat-label">Conversion</div>
                  </div>
                </div>
                <div className="team-status-text" style={{ color: getStatusColor(agent.status) }}>
                  {statusText}
                </div>
                <div className="team-actions">
                  <button className="team-btn" onClick={() => navigate(`/owner/merchants?agentId=${agent.id}`)}>
                    <Eye size={12} /> View Merchants
                  </button>
                  <button className="team-btn" onClick={() => navigate(`/owner/team?resetPIN=${agent.id}`)}>
                    <RotateCcw size={12} /> Reset PIN
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </OwnerLayout>
  );
}
